import { FC, useCallback, useState } from 'react';

import { useAuth } from '@shared/state/hooks/useAuth';

import { CouponHistorySection } from './CouponHistorySection';
import { CouponPendingSection } from './CouponPendingSection';

// ─────────────────────────────────────────────
// 상조 쿠폰형(기프티콘) 정산 — 통합 페이지(GiftSettlementAgencyPage)의 '쿠폰형' 탭 본문.
// 상조(bugoBrand)에 지급하는 쿠폰형 정산의 미정산 집행 + 내역 관리 (단계별 로드).
// 배송형은 AgencyDeliverySettlementSection, 배송업체는 GiftSettlementSupplyPage.
// ─────────────────────────────────────────────

export interface AgencyCouponSettlementSectionProps {
  /** 집행·라인편집 등으로 정산 문서가 바뀌었을 때 상위(월별 합계 탭) 재조회 알림 */
  onSettlementChanged?: () => void;
}

export const AgencyCouponSettlementSection: FC<AgencyCouponSettlementSectionProps> =
  function AgencyCouponSettlementSection({ onSettlementChanged }) {
    const { isAdmin, isGiftAgent } = useAuth();
    const canWrite = isAdmin() && !isGiftAgent();
    // GiftAgent(마리) 조회 뷰: viaMari 상조만 노출 + 쓰기 UI 숨김
    const giftAgentView = !isAdmin() && isGiftAgent();

    // 집행 후 내역 섹션 강제 리프레시
    const [historyRefresh, setHistoryRefresh] = useState(0);
    const handleSettled = useCallback(() => {
      setHistoryRefresh((n) => n + 1);
      onSettlementChanged?.();
    }, [onSettlementChanged]);

    // 정산서 라인 편집(쿠폰 제외→미정산 복귀 / 추가→미정산 이탈) 후 미정산 섹션 리프레시
    const [pendingRefresh, setPendingRefresh] = useState(0);
    const handleLinesChanged = useCallback(() => {
      setPendingRefresh((n) => n + 1);
      onSettlementChanged?.();
    }, [onSettlementChanged]);

    return (
      <div>
        {/* 미정산 내역 (상단) */}
        <section className="mb-10">
          <h2 className="mb-4 text-lg font-bold text-gray-800">미정산 내역</h2>
          {canWrite ? (
            <CouponPendingSection
              onSettled={handleSettled}
              refreshTrigger={pendingRefresh}
            />
          ) : (
            <div className="rounded-lg bg-white p-8 text-center text-gray-500 shadow">
              쿠폰 정산 집행은 관리자만 가능합니다.
            </div>
          )}
        </section>

        {/* 정산 완료 내역 (하단) */}
        <section>
          <h2 className="mb-4 text-lg font-bold text-gray-800">정산 완료 내역</h2>
          <CouponHistorySection
            isAdmin={canWrite}
            refreshTrigger={historyRefresh}
            giftAgentView={giftAgentView}
            onLinesChanged={handleLinesChanged}
          />
        </section>
      </div>
    );
  };
